import React, { useEffect, useState } from "react";
import { gql, useQuery } from "urql";
import { Link, useLocation, useParams, useSearchParams } from "wouter";
import { Formik, Form } from "formik";
import { FileSpreadsheet } from "lucide-react";

import stableStringify from "json-stable-stringify";
import { DateHelper } from "../../../common/src";
import { valueFormat } from "../../../common/src/utils/FormatFunctions";

import { removeNull } from "../../../components/src/common/Utils";
import { SubmitButton, NullableMultiSelectField, NullableToggleDateField } from "../../../components/src/form";
import { exportToXlsx2 } from "../../../components/src/common/exportToXlsx2";

import { TransactionItemFilterInput } from "../types.generated";
import { cloneDeep, orderBy } from "lodash";
import { Button } from "../../../components/src/Button";
import { ReactTable } from "../../../components/src/react-table/ReactTable";
import { SelectColumnFilter } from "../../../components/src/react-table/ReactTableFilters";
import { MiniTransactionForm, miniTransactionTypes } from "./MiniTransactionForm";
import { Panel } from "../../../components/src/common/Panel";

const GET_ClIENTS = gql`
    query getClients {
        clients: parties(filter: { typeIn: [Fund, Client], withTransactions: true }) {
            _id
            name
        }
    }
`;

const GET_TRANSACTION_ITEMS = gql`
    query transactionItems($filter: TransactionItemFilterInput) {
        transactionItems(filter: $filter) {
            clientId
            client {
                _id
                name
            }
            transactionId
            transactionStatus
            transactionTradeDate
            transactionType
            _id
            type
            amount
            quantity
            price
            currency
            performanceType
            instrumentId
            instrument {
                _id
                name
            }
            fxRate
            valueDate
            externalAccountId
            externalAccount {
                _id
                name
            }
            accountId
            account {
                _id
                name
                description
            }
            custodian {
                _id
                name
            }
            transaction {
                updateUserInfo {
                    name
                }
                description
                brokerId
                broker {
                    _id
                    name
                }
                updateTimestamp
            }
            portfolioInstrumentId
            portfolioInstrument {
                name
            }
        }
    }
`;

export function TransactionItemsPage(): React.ReactElement {
    const startDate: string = DateHelper.addDays(new Date(), -30).toISOString().slice(0, 10);
    const endDate: string = new Date().toISOString().slice(0, 10);
    const [, navigate] = useLocation();
    const [queryArgs, setQueryArgs] = useSearchParams();
    const params = useParams<{ id: string }>();

    const [showFilter, setShowFilter] = useState(false);

    const defaultClientIds = queryArgs.has("clientIds") ? queryArgs.get("clientIds").split(",") : null;

    const [filter, setFilter] = useState<Partial<TransactionItemFilterInput>>({
        clientIds: defaultClientIds,
        startDate: queryArgs.get("startDate") ? queryArgs.get("startDate") : startDate,
        endDate: queryArgs.get("endDate") ? queryArgs.get("endDate") : endDate,
        includePortfolioSwapConstituents: true
    });

    const [{ fetching: loadingClients, error: errorClients, data: dataClients }] = useQuery({
        query: GET_ClIENTS,
        requestPolicy: "cache-and-network"
    });

    const [{ fetching: loading, error, data: dataItems }] = useQuery({
        query: GET_TRANSACTION_ITEMS,
        variables: { filter },
        requestPolicy: "cache-and-network"
    });

    useEffect(() => {
        let newFilter: Partial<TransactionItemFilterInput> = {
            startDate: startDate,
            endDate: endDate,
            includePortfolioSwapConstituents: true
        };

        if (queryArgs.get("clientIds")) {
            const clientIds = queryArgs.get("clientIds").includes(",")
                ? queryArgs.get("clientIds").split(",")
                : [queryArgs.get("clientIds")];

            newFilter = {
                ...filter,
                clientIds: clientIds
            };
        }

        if (queryArgs.get("startDate")) {
            newFilter = { ...filter, startDate: queryArgs.get("startDate") };
        }
        if (queryArgs.get("endDate")) {
            newFilter = { ...filter, endDate: queryArgs.get("endDate") };
        }

        if (stableStringify(filter) !== stableStringify(newFilter)) {
            setFilter(newFilter);
            setQueryArgs(removeNull({ ...queryArgs, ...newFilter }));
            if (newFilter.clientIds !== null || newFilter.startDate !== null || newFilter.endDate !== null) {
                setShowFilter(true);
            }
        }
    }, [filter, queryArgs, startDate, endDate, setQueryArgs]);

    const columns = React.useMemo(() => {
        const accountNameExtendedByName: Record<string, { _id: string; name: string }> = {};

        if (dataItems && dataItems.transactionItems) {
            dataItems.transactionItems.forEach((item) => {
                if (item.account) {
                    accountNameExtendedByName[item.account.name] = {
                        _id: item.account.name,
                        name: item.account.name + " - " + item.account.description
                    };
                }
            });
        }
        return [
            {
                header: "Client",
                id: "client.name",
                accessorKey: "client.name",
                filter: SelectColumnFilter,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    let name = cellProps.getValue();
                    if (name && name.length > 20) {
                        name = name.slice(-20);
                    }
                    return <Link to={`/parties/${row.original.client._id}`}>{cellProps.getValue()}</Link>;
                },
                size: 100
            },
            {
                header: "Broker",
                id: "transaction.broker.name",
                accessorKey: "transaction.broker.name",
                accessorFn: (row) => (row.transaction.broker ? row.transaction.broker.name : null),
                filter: SelectColumnFilter,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.transaction.broker) {
                        return <Link to={`/parties/${cellProps.getValue()}`}>{cellProps.getValue()}</Link>;
                    } else {
                        return <div></div>;
                    }
                },
                size: 120
            },
            {
                header: "Trade date",
                accessorKey: "transactionTradeDate",
                headerStyle: { textAlign: "right" },
                cell: (cellProps) => <div style={{ textAlign: "center" }}>{cellProps.getValue()}</div>,
                size: 80
            },
            {
                header: "Value date",
                accessorKey: "valueDate",
                cell: (cellProps) => <div style={{ textAlign: "center" }}>{cellProps.getValue()}</div>,
                size: 80
            },
            {
                header: "Description",
                id: "transaction.description",
                accessorKey: "transaction.description"
            },
            {
                header: "Type",
                accessorKey: "type",
                filter: SelectColumnFilter,
                size: 100
            },
            {
                header: "Transaction type",
                accessorKey: "transactionType",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    const isMiniTransaction = miniTransactionTypes.includes(cellProps.getValue());
                    return isMiniTransaction && row.original ? (
                        <Link
                            to={`/transactionitems/${row.original.transactionId}${queryArgs.toString() ? `?${queryArgs.toString()}` : ""}`}
                        >
                            {cellProps.getValue()}
                        </Link>
                    ) : (
                        <div>{cellProps.getValue()}</div>
                    );
                },
                filter: SelectColumnFilter,
                size: 95
            },
            {
                header: "Status",
                accessorKey: "transactionStatus",
                filter: SelectColumnFilter,
                size: 60
            },
            {
                header: "Quantity",
                accessorKey: "quantity",
                filterFn: "startsWith",
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{valueFormat(cellProps.getValue())}</div>,
                size: 70
            },
            {
                header: "Amount",
                accessorKey: "amount",
                filterFn: "startsWith",
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{valueFormat(cellProps.getValue())}</div>,
                size: 70
            },
            {
                header: "Price",
                accessorKey: "price",
                filterFn: "startsWith",
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{valueFormat(cellProps.getValue())}</div>,
                size: 70
            },
            {
                header: "Currency",
                accessorKey: "currency",
                filter: SelectColumnFilter,
                size: 40
            },
            {
                header: "Performance type",
                accessorKey: "performanceType",
                filter: SelectColumnFilter,
                size: 70
            },
            {
                header: "Instrument",
                id: "instrument.name",
                accessorKey: "instrument.name",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.instrument) {
                        if (row.original.instrument.__typename === "Instrument") {
                            return <Link to={`/instruments/${row.original.instrumentId}`}>{cellProps.getValue()}</Link>;
                        } else {
                            return (
                                <Link to={`/parties/${row.original.clientId}/instruments/${row.original.instrumentId}`}>
                                    {cellProps.getValue()}
                                </Link>
                            );
                        }
                    } else {
                        return <div></div>;
                    }
                },
                size: 100
            },
            {
                header: "Transaction id",
                accessorKey: "transactionId",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return (
                        <a href={`/transaction/${row.original.transactionId}`} target="_blank" rel="noopener noreferrer">
                            {cellProps.getValue()}
                        </a>
                    );
                },
                size: 50
            },
            {
                header: "External account",
                id: "externalAccount.name",
                accessorKey: "externalAccount.name",
                accessorFn: (row) => (row.externalAccount ? row.externalAccount.name : null),
                filter: SelectColumnFilter,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return (
                        <Link to={`/parties/${row.original.client._id}/externalAccounts/${row.original.externalAccountId}`}>
                            {cellProps.getValue()}
                        </Link>
                    );
                },
                size: 100
            },
            {
                header: "Account",
                id: "account.name",
                accessorKey: "account.name",
                filter: SelectColumnFilter,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return (
                        <Link to={`/parties/${row.original.client._id}/accounts/${row.original.accountId}`}>
                            {accountNameExtendedByName[cellProps.getValue()].name}
                        </Link>
                    );
                },
                size: 100
            },
            {
                header: "Updated by",
                id: "transaction.updateUserInfo.name",
                accessorKey: "transaction.updateUserInfo.name",
                accessorFn: (row) => (row.transaction.updateUserInfo ? row.transaction.updateUserInfo.name : null),
                filter: SelectColumnFilter
            },
            {
                header: "Update timestamp",
                id: "transaction.updateTimestamp",
                accessorKey: "transaction.updateTimestamp"
            },
            {
                header: "Portfolio Instrument",
                id: "portfolioInstrument",
                accessorKey: "portfolioInstrument.name",
                size: 100
            }
        ];
    }, [dataItems, queryArgs]);

    if (loading || loadingClients) return <div>Loading...</div>;
    if (error)
        return (
            <div>
                <p>error:</p>
                <pre> {JSON.stringify(error, null, 2)}</pre>
            </div>
        );
    if (errorClients)
        return (
            <div>
                <p>error:</p>
                <pre> {JSON.stringify(errorClients, null, 2)}</pre>
            </div>
        );

    const clientsById: Record<string, { key: string; value: string }> = {};

    for (const client of dataClients.clients) {
        clientsById[client._id] = { key: client._id, value: client.name };
    }

    if (Boolean(queryArgs.get("clientIds")) && !clientsById[queryArgs.get("clientIds")]) {
        return (
            <div>
                <p>
                    No transaction items exists for clientId {queryArgs.get("clientIds")}. Go to{" "}
                    {<Link to={`/transactionitems`}>{"this page"}</Link>} to see transaction items for other clients.
                </p>
            </div>
        );
    }

    const toggleFilter = () => {
        setShowFilter(!showFilter);
    };

    const transactionItemsOrdered = orderBy(dataItems.transactionItems, "transactionTradeDate", "desc");

    return (
        <div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">
                    {showFilter ? (
                        <Button variant="ghost" className="underline text-sky-600 hover:text-sky-800 border-0 p-0" onClick={toggleFilter}>
                            Hide filter
                        </Button>
                    ) : (
                        <Button variant="ghost" className="underline text-sky-600 hover:text-sky-800 border-0 p-0" onClick={toggleFilter}>
                            Show filter
                        </Button>
                    )}
                </div>
            </div>
            {showFilter ? (
                <div className="flex flex-wrap -mx-2 ml-1">
                    <div className="w-full px-2 mb-4">
                        <Formik
                            enableReinitialize={true}
                            initialValues={filter}
                            validate={() => {
                                const errors: any = {};
                                return Object.keys(errors).length > 0 ? errors : {};
                            }}
                            onSubmit={async (submitValues) => {
                                const newValues: Partial<TransactionItemFilterInput> = removeNull(submitValues);
                                setQueryArgs(newValues as any);
                                setFilter({ ...newValues, includePortfolioSwapConstituents: true });
                            }}
                        >
                            {({ isSubmitting, values: temp }) => {
                                const _values: Partial<TransactionItemFilterInput> = temp;
                                return (
                                    <Form autoComplete="off">
                                        <div className="flex flex-wrap -mx-2">
                                            <div className="w-full md:w-4/12 px-2 mb-4">
                                                <div className="flex flex-wrap -mx-2 mt-4">
                                                    <div className="w-full px-2 mb-4">
                                                        <NullableMultiSelectField
                                                            name="clientIds"
                                                            label={"Client"}
                                                            className=""
                                                            disabled={isSubmitting}
                                                            options={Object.values(clientsById)}
                                                            size={5}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="w-full px-2 mb-4">
                                                <div className="flex flex-wrap -mx-2 mt-4">
                                                    <div className="w-full px-2 mb-4">
                                                        <NullableToggleDateField
                                                            name="startDate"
                                                            label="Start date"
                                                            disabled={isSubmitting}
                                                        />
                                                        <NullableToggleDateField
                                                            name="endDate"
                                                            label="End date"
                                                            className="mt-3"
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap -mx-2 mb-4">
                                            <div className="w-full md:w-4/12 px-2 mb-4">
                                                <div className="flex flex-wrap -mx-2 mt-4">
                                                    <div className="w-full px-2 mb-4">
                                                        <SubmitButton className="btn btn-primary" disabled={loading} label={"Apply"} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Form>
                                );
                            }}
                        </Formik>
                    </div>
                </div>
            ) : null}

            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">
                    <button
                        id="export-excel"
                        className="export-link"
                        type="button"
                        onClick={() => {
                            const items = orderBy(cloneDeep(dataItems.transactionItems), "transactionTradeDate", "desc");
                            items.forEach((item) => {
                                item.clientName = item.client ? item.client.name : "";
                                item.externalAccountName = item.externalAccount ? item.externalAccount.name : "";
                                item.brokerName = item.transaction.broker ? item.transaction.broker.name : "";
                                item.custodianName = item.custodian ? item.custodian.name : "";
                                item.accountName = item.account ? item.account.name : "";
                                item.instrumentName = item.instrument ? item.instrument.name : "";
                                item.updateUserName = item.transaction.updateUserInfo ? item.transaction.updateUserInfo.name : "";
                                item.updateTimestamp = item.transaction.updateTimestamp ? item.transaction.updateTimestamp : "";

                                item.status = item.transactionStatus;
                                item.tradeDate = item.transactionTradeDate;

                                delete item.client;
                                delete item.externalAccount;
                                delete item.custodian;
                                delete item.account;
                                delete item.instrument;
                                delete item.transaction;
                                delete item.transactionStatus;
                                delete item.transactionTradeDate;
                            });

                            exportToXlsx2(items, "TransactionItems.xlsx");
                        }}
                    >
                        <FileSpreadsheet className="Excel" />
                    </button>
                </div>
            </div>
            <div className="flex flex-wrap -mx-2 ml-2">
                <ReactTable
                    columns={columns}
                    data={transactionItemsOrdered}
                    defaultHiddenColumns={[
                        "transaction.description",
                        "transaction.broker.name",
                        "account.name",
                        "externalAccount.name",
                        "transaction.updateTimestamp"
                    ]}
                />
                <Panel
                    isOpen={params.id != null}
                    onDismiss={() => navigate(`/transactionitems${queryArgs.toString() ? `?${queryArgs.toString()}` : ""}`)}
                >
                    {params.id ? <MiniTransactionForm id={params.id} type={null} showHeader /> : null}
                </Panel>
            </div>
        </div>
    );
}
